import { useEffect, useState } from 'react';
import { Check, Mic, Users, RefreshCcw } from 'lucide-react';
import { settingsAPI } from '../services/api.js';
import ErrorBanner from './ErrorBanner.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';

// Live, reversible engagement switch for the "Alfred Mens Health" tenant.
// Flips a single tenant between two engagements; the change takes effect on the
// NEXT call. BOTH configurations are always preserved server-side — this control
// only chooses which one is active.
const ENGAGEMENTS = [
  {
    value: 'mens-health',
    label: "Men's Health",
    sub: 'Single voice',
    icon: Mic,
    desc: 'The live default — one concierge voice (Jose / Collin / Allen) on the call.'
  },
  {
    value: 'vitamd-landing',
    label: 'VitaMD Landing',
    sub: 'Two-AI panel',
    icon: Users,
    desc: 'Two AIs on the same call: Barbara (guide) and Doctor Sanchez (clinician).'
  }
];

export default function EngagementSwitch() {
  // Engagement is a Men's Health / VitaMD-only feature (the options below are hardcoded for
  // that tenant). Only render it there, so it doesn't bleed into Alfred Voice / Bacardi /
  // Learner (which have no engagement concept).
  const { selectedTenant, tenant } = useAuth();
  const _t = selectedTenant || tenant;
  const isEngagementTenant =
    _t?.subdomain === 'menshealth' || /vita\s?md|men'?s\s*health/i.test(_t?.companyName || '');
  const [active, setActive] = useState('mens-health');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await settingsAPI.getAiConfig();
      const data = res?.data || res || {};
      setActive(data.activeEngagement || 'mens-health');
    } catch (err) {
      setError(err.message || 'Failed to load engagement state.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEngagementTenant) load();
  }, [isEngagementTenant]);

  const onSwitch = async (value) => {
    if (value === active || saving) return;
    const target = ENGAGEMENTS.find((e) => e.value === value);
    const ok = window.confirm(
      `Switch the active engagement to "${target.label} — ${target.sub}"?\n\n` +
        'This takes effect on the NEXT call. The current call (if any) is unaffected, ' +
        'and the other configuration is preserved — you can switch back at any time.'
    );
    if (!ok) return;
    setSaving(value);
    setError('');
    try {
      const res = await settingsAPI.setEngagement(value);
      const next = res?.data?.activeEngagement || value;
      setActive(next);
      setSavedAt(new Date());
    } catch (err) {
      setError(err.message || 'Failed to switch engagement.');
    } finally {
      setSaving('');
    }
  };

  if (!isEngagementTenant) return null;

  return (
    <section className="card mb-8">
      <div className="flex items-start justify-between gap-4 mb-1">
        <h2 className="text-xl text-stone-100">Engagement</h2>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="btn-ghost"
        >
          <RefreshCcw size={14} />
          Refresh
        </button>
      </div>
      <p className="text-sm text-stone-400 mb-5">
        Choose which engagement is live. The switch takes effect on the{' '}
        <strong className="text-stone-200">next call</strong> — both setups stay
        configured, so you can flip back anytime.
      </p>

      {error && (
        <div className="mb-4">
          <ErrorBanner message={error} />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {ENGAGEMENTS.map((e) => {
          const isActive = active === e.value;
          const isSaving = saving === e.value;
          const Icon = e.icon;
          return (
            <button
              key={e.value}
              type="button"
              onClick={() => onSwitch(e.value)}
              disabled={loading || !!saving || isActive}
              aria-pressed={isActive}
              className={[
                'text-left rounded-lg border p-4 transition',
                isActive
                  ? 'border-alfred-gold bg-alfred-gold/10 ring-1 ring-alfred-gold'
                  : 'border-stone-700 bg-stone-900/40 hover:border-stone-500',
                loading || saving ? 'opacity-70' : 'cursor-pointer'
              ].join(' ')}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-stone-100">
                  <Icon size={18} />
                  <span className="font-medium">{e.label}</span>
                </div>
                {isActive ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-alfred-gold">
                    <Check size={14} /> Active
                  </span>
                ) : isSaving ? (
                  <span className="text-xs text-stone-400">Switching…</span>
                ) : null}
              </div>
              <div className="text-xs uppercase tracking-wide text-stone-400 mb-1">
                {e.sub}
              </div>
              <p className="text-sm text-stone-400">{e.desc}</p>
            </button>
          );
        })}
      </div>

      {savedAt && (
        <p className="mt-4 text-xs text-stone-500">
          Switched {savedAt.toLocaleTimeString()} — effective on the next call.
        </p>
      )}
    </section>
  );
}
